import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { formatCurrencyUY } from "../utils/formatters";
import { getAllActiveFunds } from "../utils/fundHelpers";
import {
  buildAccountMovements,
  computeCurrentFundBalances,
  buildFundBalanceSeries,
} from "../utils/fundMovements";
import { resolveCierreWindow } from "../utils/cierrePeriod";
import { AppScreen, AppHeader, AppCard, SegmentedControl, Money, AppEmptyState, AppSpinner } from "../components/app";
import EmptyState from "../components/EmptyState";
import { appColors, appType, appSpacing, appRadius } from "../utils/appTheme";
import { colors, spacing, radius, shadows, typography } from "../utils/designTokens";
import { useIsMobile } from "../hooks/useResponsive";
import { useTranslation } from "react-i18next";
import {
  FaWallet,
  FaArrowUp,
  FaArrowDown,
  FaChevronLeft,
  FaChevronRight,
  FaReceipt,
  FaHome,
  FaGavel,
  FaMoneyBillWave,
  FaUniversity,
  FaSpinner,
} from "react-icons/fa";

/**
 * Cuentas del edificio (vista del propietario, dentro del hub Finanzas).
 *
 * Muestra, para cada fondo/cuenta configurada del edificio (Caja, Reserva y
 * fondos personalizados como Garaje):
 *   - Saldo actual de cada cuenta (tarjetas seleccionables).
 *   - Evolución mes a mes del saldo del fondo seleccionado (6 / 12 / 18 meses).
 *   - Movimientos del mes: los cobros de gastos comunes AGRUPADOS en una línea
 *     y cada gasto DESGLOSADO, más ingresos y multas cobradas.
 *
 * Reutiliza el motor de `fundMovements.js` (misma verdad que el panel admin).
 */
const CuentasEdificio = ({ setSelected, goBack }) => {
  const { t } = useTranslation("cuentasEdificio");
  const MONTHS = t("months", { returnObjects: true });
  const rangeOptions = useMemo(
    () => [
      { value: 6, label: t("range.6") },
      { value: 12, label: t("range.12") },
      { value: 18, label: t("range.18") },
    ],
    [t]
  );
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const buildingId = selectedBuilding?.buildingid;
  // Edificio de solo lectura (ex-ocupante): las reglas 0D le dejan leer solo sus
  // pagos, no los de todo el edificio. No se consulta nada y se muestra un aviso
  // (el menú ya no ofrece esta pantalla; esto cubre un acceso directo).
  const soloLectura = !!selectedBuilding?.isReadOnly;

  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [multas, setMultas] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [building, setBuilding] = useState(null);

  const [activeFund, setActiveFund] = useState("caja");
  const [evolutionMonths, setEvolutionMonths] = useState(6);
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      if (!buildingId || soloLectura) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // El catálogo de proveedores va aparte y es opcional: la demo no lo lee
        // (reglas 0D) y su rechazo no tiene que tirar el resto de la carga. Sin
        // él, los gastos se muestran con su descripción.
        const proveedores = getDocs(collection(db, "supplier"))
          .then((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })))
          .catch((e) => {
            console.warn("Sin catálogo de proveedores:", e?.code || e?.message);
            return [];
          });
        const [expSnap, paySnap, paySnap2, multSnap, incSnap, aptSnap, bldSnap, supList] = await Promise.all([
          getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "payments"), where("buildingId", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "multas"), where("buildingid", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingId)))),
          proveedores,
        ]);
        if (cancelled) return;

        setExpenses(expSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const payMap = new Map();
        paySnap.docs.forEach((d) => payMap.set(d.id, { id: d.id, ...d.data() }));
        paySnap2.docs.forEach((d) => { if (!payMap.has(d.id)) payMap.set(d.id, { id: d.id, ...d.data() }); });
        setAllPayments(Array.from(payMap.values()));
        setMultas(multSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setIncomes(incSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setApartments(aptSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setBuilding(bldSnap.docs[0]?.data() || null);
        setSuppliers(supList);
      } catch (err) {
        console.error("Error cargando cuentas del edificio:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [buildingId, soloLectura]);

  const expensePayments = useMemo(() => allPayments.filter((p) => p.expensesid != null), [allPayments]);
  const residentPayments = useMemo(() => allPayments.filter((p) => p.userid && p.month != null), [allPayments]);

  const funds = useMemo(() => getAllActiveFunds(building), [building]);
  // Si el fondo activo dejó de existir, volver a caja.
  useEffect(() => {
    if (funds.length && !funds.some((f) => f.id === activeFund)) setActiveFund("caja");
  }, [funds, activeFund]);

  const currentBalances = useMemo(
    () => computeCurrentFundBalances({ expenses, expensePayments, residentPayments, multas, incomes, building, currency: "peso" }),
    [expenses, expensePayments, residentPayments, multas, incomes, building]
  );

  const engineArgs = useMemo(
    () => ({ expenses, expensePayments, residentPayments, multas, incomes, suppliers, apartments, building, fund: activeFund, currency: "peso" }),
    [expenses, expensePayments, residentPayments, multas, incomes, suppliers, apartments, building, activeFund]
  );

  // Ventana del período (fecha de corte configurable). Si el edificio tiene día de
  // corte, los movimientos del mes se bucketean por la ventana (ej. septiembre =
  // 05/09→05/10) → un gasto/cobro del 04/10 se muestra en septiembre. Sin corte,
  // mes calendario (comportamiento histórico).
  const diaCorte = building?.diaCorte ?? null;
  const periodWindow = useMemo(
    () => resolveCierreWindow(month, year, { diaCorte }),
    [month, year, diaCorte]
  );

  const balanceSeries = useMemo(
    () => buildFundBalanceSeries({ ...engineArgs, months: evolutionMonths, now, diaCorte }),
    [engineArgs, evolutionMonths, now, diaCorte]
  );

  const movementData = useMemo(
    () => buildAccountMovements({ ...engineArgs, year, month, periodStart: diaCorte ? periodWindow.start : null, periodEnd: diaCorte ? periodWindow.end : null }),
    [engineArgs, year, month, diaCorte, periodWindow]
  );

  // Agrupar movimientos: cobros de GC en una sola línea; gastos, ingresos y
  // multas desglosados.
  const grouped = useMemo(() => {
    const movs = movementData.movements || [];
    let cobrosGC = 0;
    const gastos = [];
    const ingresos = [];
    const multasCobradas = [];
    movs.forEach((m) => {
      if (m.type === "COBRANZA") cobrosGC += m.credit || 0;
      else if (m.type === "GASTO") gastos.push(m);
      else if (m.type === "INGRESO") ingresos.push(m);
      else if (m.type === "MULTA COBRADA") multasCobradas.push(m);
    });
    return { cobrosGC, gastos, ingresos, multasCobradas };
  }, [movementData]);

  const activeFundObj = funds.find((f) => f.id === activeFund) || funds[0];
  const fundColor = activeFundObj?.color || appColors.blue;

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1);
  };
  const isCurrentOrFuture = year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);

  const backTo = goBack || (setSelected ? () => setSelected("finanzas") : undefined);

  const hasAnyMovement =
    grouped.cobrosGC > 0 || grouped.gastos.length > 0 || grouped.ingresos.length > 0 || grouped.multasCobradas.length > 0;

  const avisoSoloLectura = {
    title: t("readOnly.title", { defaultValue: "Cuentas no disponibles" }),
    description: t("readOnly.description", {
      defaultValue:
        "Tu vínculo con este edificio terminó: podés ver tus pagos en Estado de cuenta, pero no las cuentas del edificio.",
    }),
  };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE — app de residente (toolkit compacto)
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) return (
    <AppScreen
      header={
        <AppHeader
          title={t("header.title")}
          subtitle={selectedBuilding?.name || t("header.subtitle")}
          onBack={backTo}
        />
      }
    >
      {soloLectura ? (
        <AppEmptyState icon={<FaUniversity />} {...avisoSoloLectura} />
      ) : loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <AppSpinner />
        </div>
      ) : (
        <>
          {/* ── Saldo actual de cada cuenta (tarjetas seleccionables) ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: appSpacing.gridGap,
              marginBottom: appSpacing.section,
            }}
          >
            {funds.map((f) => {
              const selected = f.id === activeFund;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFund(f.id)}
                  style={{
                    textAlign: "left",
                    background: appColors.surface,
                    border: `1.5px solid ${selected ? f.color : appColors.line2}`,
                    borderRadius: appRadius.cardSm,
                    padding: 14,
                    cursor: "pointer",
                    boxShadow: selected ? `0 2px 10px ${f.color}22` : "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: f.color, flexShrink: 0 }} />
                    <span style={{ ...appType.statLabel, color: appColors.ink2, overflowWrap: "anywhere" }}>{f.name}</span>
                  </span>
                  <Money value={currentBalances[f.id] || 0} size={19} />
                </button>
              );
            })}
          </div>

          {/* ── Evolución del saldo ── */}
          <AppCard style={{ marginBottom: appSpacing.section }} padding={18}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              <span style={{ ...appType.cardTitle, minWidth: 0 }}>
                {t("evolution.title", { fund: activeFundObj?.name })}
              </span>
              <div style={{ maxWidth: 260, flex: "1 1 200px" }}>
                <SegmentedControl options={rangeOptions} value={evolutionMonths} onChange={setEvolutionMonths} />
              </div>
            </div>
            <BalanceBars series={balanceSeries} color={fundColor} />
          </AppCard>

          {/* ── Movimientos del mes ── */}
          <AppCard padding={18}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <span style={{ ...appType.cardTitle }}>{t("movements.title")}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <NavBtn onClick={prevMonth} aria-label={t("nav.prevMonth")}><FaChevronLeft size={12} /></NavBtn>
                <span style={{ ...appType.bodySm, color: appColors.ink, minWidth: 96, textAlign: "center" }}>
                  {MONTHS[month - 1]} {year}
                </span>
                <NavBtn onClick={nextMonth} disabled={isCurrentOrFuture} aria-label={t("nav.nextMonth")}>
                  <FaChevronRight size={12} />
                </NavBtn>
              </div>
            </div>

            {/* Saldo inicial */}
            <SummaryRow label={t("movements.openingBalanceMonth")} value={movementData.saldoInicial} strong />

            {!hasAnyMovement ? (
              <AppEmptyState
                icon={<FaWallet />}
                title={t("empty.title")}
                description={t("empty.description")}
                style={{ marginTop: 12 }}
              />
            ) : (
              <>
                {/* Créditos */}
                {(grouped.cobrosGC > 0 || grouped.ingresos.length > 0 || grouped.multasCobradas.length > 0) && (
                  <div style={{ marginTop: 14 }}>
                    <SectionCaption icon={<FaArrowUp />} color={appColors.green}>{t("movements.income")}</SectionCaption>
                    {grouped.cobrosGC > 0 && (
                      <MovementRow
                        icon={<FaHome />}
                        title={t("movements.commonChargesCollection")}
                        subtitle={t("movements.allUnitsMonth")}
                        amount={grouped.cobrosGC}
                        tone="green"
                      />
                    )}
                    {grouped.ingresos.map((m, i) => (
                      <MovementRow
                        key={`ing-${i}`}
                        icon={<FaMoneyBillWave />}
                        title={m.description || t("movements.incomeFallback")}
                        amount={m.credit}
                        tone="green"
                      />
                    ))}
                    {grouped.multasCobradas.map((m, i) => (
                      <MovementRow
                        key={`mul-${i}`}
                        icon={<FaGavel />}
                        title={m.description || t("movements.fineCollectedFallback")}
                        amount={m.credit}
                        tone="green"
                      />
                    ))}
                  </div>
                )}

                {/* Débitos: cada gasto desglosado */}
                {grouped.gastos.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <SectionCaption icon={<FaArrowDown />} color={appColors.red}>{t("movements.expenses")}</SectionCaption>
                    {grouped.gastos.map((m, i) => (
                      <MovementRow
                        key={`gas-${i}`}
                        icon={<FaReceipt />}
                        title={m.description || t("movements.expenseFallback")}
                        subtitle={m.ref}
                        amount={m.debit}
                        tone="red"
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Totales + saldo final */}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${appColors.line}`, display: "flex", flexDirection: "column", gap: 8 }}>
              <SummaryRow label={t("movements.totalIncomeMonth")} value={movementData.totalCreditos} tone="green" />
              <SummaryRow label={t("movements.totalExpensesMonth")} value={movementData.totalDebitos} tone="red" />
              <SummaryRow label={t("movements.closingBalanceMonth")} value={movementData.saldoFinal} strong />
            </div>
          </AppCard>
        </>
      )}
    </AppScreen>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP — layout ancho que aprovecha el espacio horizontal
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div
      style={{
        padding: 24,
        minHeight: "100vh",
        background: "#f5f7fa",
        display: "flex",
        flexDirection: "column",
        gap: spacing[5],
        fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: colors.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
          <FaUniversity />
        </div>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 400, margin: 0, color: colors.primary }}>{t("header.title")}</h1>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "2px 0 0" }}>{selectedBuilding?.name || t("header.subtitleDesktop")}</p>
        </div>
      </div>

      {soloLectura ? (
        <EmptyState icon={FaUniversity} {...avisoSoloLectura} />
      ) : loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "64px 0", color: colors.gray400, fontSize: 14 }}>
          <FaSpinner className="spin" /> {t("loading")}
        </div>
      ) : (
        <>
          {/* Saldos por cuenta (tarjetas seleccionables) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: spacing[4] }}>
            {funds.map((f) => {
              const selected = f.id === activeFund;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFund(f.id)}
                  style={{
                    textAlign: "left",
                    background: colors.bgCard,
                    border: `2px solid ${selected ? f.color : "#e5e7eb"}`,
                    borderRadius: radius.md,
                    padding: 18,
                    cursor: "pointer",
                    boxShadow: selected ? `0 4px 16px ${f.color}22` : shadows.sm,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    transition: "border-color .15s, box-shadow .15s",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: f.color }} />
                    <span style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px" }}>{f.name}</span>
                  </span>
                  <span style={{ fontSize: 24, fontWeight: 400, color: colors.black, fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrencyUY(currentBalances[f.id] || 0)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Evolución del saldo */}
          <div style={{ background: colors.bgCard, borderRadius: radius.md, padding: spacing[5], boxShadow: shadows.sm }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: typography.sizes.lg, fontWeight: 400, margin: 0, color: colors.black }}>
                {t("evolution.title", { fund: activeFundObj?.name })}
              </h2>
              <div style={{ maxWidth: 300, flex: "1 1 220px" }}>
                <SegmentedControl options={rangeOptions} value={evolutionMonths} onChange={setEvolutionMonths} />
              </div>
            </div>
            <BalanceBars series={balanceSeries} color={fundColor} height={230} showValues />
          </div>

          {/* Movimientos del mes: dos columnas (Ingresos | Gastos) */}
          <div style={{ background: colors.bgCard, borderRadius: radius.md, padding: spacing[5], boxShadow: shadows.sm }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: typography.sizes.lg, fontWeight: 400, margin: 0, color: colors.black }}>{t("movements.title")}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <NavBtn onClick={prevMonth} aria-label={t("nav.prevMonth")}><FaChevronLeft size={12} /></NavBtn>
                <span style={{ fontSize: 14, color: colors.black, minWidth: 120, textAlign: "center" }}>{MONTHS[month - 1]} {year}</span>
                <NavBtn onClick={nextMonth} disabled={isCurrentOrFuture} aria-label={t("nav.nextMonth")}><FaChevronRight size={12} /></NavBtn>
              </div>
            </div>

            {/* Resumen del mes */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[3], marginBottom: 20 }}>
              <DSummaryCell label={t("movements.openingBalance")} value={movementData.saldoInicial} />
              <DSummaryCell label={t("movements.incomeMonth")} value={movementData.totalCreditos} color={colors.success} />
              <DSummaryCell label={t("movements.expensesMonth")} value={movementData.totalDebitos} color={colors.error} />
              <DSummaryCell label={t("movements.closingBalance")} value={movementData.saldoFinal} strong />
            </div>

            {!hasAnyMovement ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: colors.gray400, fontSize: 14 }}>
                {t("empty.description")}
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[5], alignItems: "start" }}>
                {/* Ingresos */}
                <div>
                  <DColHeader icon={<FaArrowUp />} color={colors.success}>{t("movements.income")}</DColHeader>
                  {grouped.cobrosGC > 0 && (
                    <DMovRow title={t("movements.commonChargesCollection")} subtitle={t("movements.allUnitsMonth")} amount={grouped.cobrosGC} color={colors.success} />
                  )}
                  {grouped.ingresos.map((m, i) => (
                    <DMovRow key={`ing-${i}`} title={m.description || t("movements.incomeFallback")} subtitle={m.ref} amount={m.credit} color={colors.success} />
                  ))}
                  {grouped.multasCobradas.map((m, i) => (
                    <DMovRow key={`mul-${i}`} title={m.description || t("movements.fineCollectedFallback")} subtitle={m.ref} amount={m.credit} color={colors.success} />
                  ))}
                  {grouped.cobrosGC === 0 && grouped.ingresos.length === 0 && grouped.multasCobradas.length === 0 && (
                    <div style={{ fontSize: 13, color: colors.gray400, padding: "10px 0" }}>{t("movements.noIncome")}</div>
                  )}
                </div>
                {/* Gastos */}
                <div>
                  <DColHeader icon={<FaArrowDown />} color={colors.error}>{t("movements.expenses")}</DColHeader>
                  {grouped.gastos.length > 0 ? (
                    grouped.gastos.map((m, i) => (
                      <DMovRow key={`gas-${i}`} title={m.description || t("movements.expenseFallback")} subtitle={m.ref} amount={m.debit} color={colors.error} />
                    ))
                  ) : (
                    <div style={{ fontSize: 13, color: colors.gray400, padding: "10px 0" }}>{t("movements.noExpenses")}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ── Sub-componentes de presentación ─────────────────────────────────────── */

const BalanceBars = ({ series = [], color, height = 150, showValues = false }) => {
  const { t } = useTranslation("cuentasEdificio");
  const hasData = series.some((s) => s.saldo !== 0);
  const maxAbs = Math.max(...series.map((s) => Math.abs(s.saldo)), 1);
  const H = height;
  if (!hasData) {
    return (
      <div style={{ ...appType.bodySm, color: appColors.ink3, textAlign: "center", padding: "28px 8px" }}>
        {t("chart.noData")}
      </div>
    );
  }
  const labelValues = showValues && series.length <= 12;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: series.length >= 12 ? 4 : 8,
          height: H,
          borderBottom: `1px solid ${appColors.line}`,
          paddingBottom: 1,
        }}
      >
        {series.map((s) => {
          const neg = s.saldo < 0;
          const barH = Math.max(Math.round((Math.abs(s.saldo) / maxAbs) * (H - (labelValues ? 26 : 22))), s.saldo !== 0 ? 4 : 0);
          return (
            <div
              key={`${s.month}-${s.year}`}
              title={`${s.label} ${s.year}: ${formatCurrencyUY(s.saldo)}`}
              style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}
            >
              {labelValues && s.saldo !== 0 && (
                <span style={{ fontSize: 9.5, color: appColors.ink3, whiteSpace: "nowrap", marginBottom: 3, fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrencyUY(s.saldo)}
                </span>
              )}
              <div
                style={{
                  width: series.length >= 12 ? "80%" : 28,
                  maxWidth: 40,
                  height: barH,
                  background: neg ? appColors.red : color,
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: series.length >= 12 ? 3 : 6, marginTop: 5 }}>
        {series.map((s) => {
          const showYear = s.month === 1;
          return (
            <div key={`l-${s.month}-${s.year}`} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <span style={{ fontSize: series.length >= 12 ? 8.5 : 10, color: appColors.ink3, whiteSpace: "nowrap" }}>{s.label}</span>
              {showYear && <div style={{ fontSize: 8, color: appColors.ink3 }}>{String(s.year).slice(2)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NavBtn = ({ children, onClick, disabled, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      width: 28,
      height: 28,
      borderRadius: 8,
      border: `1px solid ${appColors.line}`,
      background: appColors.surface,
      color: appColors.ink2,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.35 : 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    {...rest}
  >
    {children}
  </button>
);

const SectionCaption = ({ icon, color, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
    <span style={{ color, fontSize: 11, display: "inline-flex" }}>{icon}</span>
    <span style={{ ...appType.statLabel, color: appColors.ink2 }}>{children}</span>
  </div>
);

const MovementRow = ({ icon, title, subtitle, amount, tone }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 0",
      borderBottom: `1px solid ${appColors.line2}`,
    }}
  >
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: 9,
        background: appColors.surface2,
        color: appColors.ink2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
    <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
      <span style={{ ...appType.body, overflowWrap: "anywhere" }}>{title}</span>
      {subtitle && <span style={{ ...appType.caption, overflowWrap: "anywhere" }}>{subtitle}</span>}
    </span>
    <span style={{ flexShrink: 0 }}>
      <Money value={amount} size={14} tone={tone} />
    </span>
  </div>
);

const SummaryRow = ({ label, value, tone, strong }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
    <span style={{ ...(strong ? appType.body : appType.bodySm), color: strong ? appColors.ink : appColors.ink2 }}>{label}</span>
    <Money value={value} size={strong ? 16 : 14} tone={tone} />
  </div>
);

/* ── Sub-componentes desktop (designTokens) ──────────────────────────────── */

const DSummaryCell = ({ label, value, color, strong }) => (
  <div style={{ background: "#f8f9fa", border: "1px solid #eef0f2", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
    <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</span>
    <span style={{ fontSize: strong ? 20 : 17, fontWeight: 400, color: color || colors.black, fontVariantNumeric: "tabular-nums" }}>
      {formatCurrencyUY(value)}
    </span>
  </div>
);

const DColHeader = ({ icon, color, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8, marginBottom: 6, borderBottom: `2px solid ${color}22` }}>
    <span style={{ color, fontSize: 13, display: "inline-flex" }}>{icon}</span>
    <span style={{ fontSize: 13, color: colors.black, textTransform: "uppercase", letterSpacing: "0.5px" }}>{children}</span>
  </div>
);

const DMovRow = ({ title, subtitle, amount, color }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0", borderBottom: "1px solid #f0f2f4" }}>
    <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 13.5, color: colors.black, overflowWrap: "anywhere" }}>{title}</span>
      {subtitle && <span style={{ fontSize: 11.5, color: "#9ca3af", overflowWrap: "anywhere" }}>{subtitle}</span>}
    </span>
    <span style={{ fontSize: 14, color: color || colors.black, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
      {formatCurrencyUY(amount)}
    </span>
  </div>
);

export default CuentasEdificio;
